import { span, type ChildNode, type ElProps, type Mountable } from '@llui/dom';

/**
 * `<Badge>` — a generic count / label / type chip (per `30-design-system.md §8`).
 *
 * The neutral counterpart to the semantic `statusPill` / `revealChip` /
 * `provenanceBadge` atoms: those encode domain meaning (fact status,
 * visibility, authorship); `badge` is the catch-all for the scattered
 * count chips ("12"), label tags ("homebrew"), and type markers that
 * carry no fact semantics. Tones are presentational, not semantic — a
 * `warn` badge is "this needs attention", not "auditor warning".
 *
 * UNREVIEWED (Phase A): minimal baseline; flagged in `/ui-gallery`.
 *
 * A11y: a colored badge is decorative chrome — the meaning must also be
 * in the text (the count, the label). Callers wanting a non-text signal
 * pass a leading glyph in `children` (the gallery's own UNREVIEWED badge
 * does exactly this with ⚠). Arbitrary attributes ride through `attrs`.
 */

export type BadgeTone = 'neutral' | 'accent' | 'info' | 'warn' | 'success';

export interface BadgeOptions {
  /** Presentational tone. Defaults to `'neutral'`. */
  tone?: BadgeTone;
  /** Badge content — a count, a label, or a glyph + label. */
  children: readonly ChildNode[];
  /** Forwarded `data-testid`. */
  testid?: string;
  /** Arbitrary extra attributes merged onto the root (spread first). */
  attrs?: ElProps;
}

export function badge(opts: BadgeOptions): Mountable {
  const tone: BadgeTone = opts.tone ?? 'neutral';
  const props: ElProps = {
    ...(opts.attrs ?? {}),
    class: `dl-badge dl-badge--${tone}`,
    'data-tone': tone,
    ...(opts.testid !== undefined ? { 'data-testid': opts.testid } : {}),
  };
  return span(props, opts.children);
}
